import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { PencilIcon, SaveIcon, Settings2Icon } from "lucide-react"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"

import { Label } from "@/components/ui/label"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { authorizedFetch } from "@/lib/api"
import { usePermissions } from "@/hooks/use-permissions"

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

type AppSetting = {
  id: string
  key: string
  value: unknown
  category: string
  description: string | null
  isPublic: boolean
  isEditable: boolean
  updatedAt: string
}

function formatValue(value: unknown) {
  if (typeof value === "string") return value
  return JSON.stringify(value)
}

export default function AppSettingsPage() {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const [editing, setEditing] = useState<AppSetting | null>(null)
  const [valueText, setValueText] = useState("")
  const [formError, setFormError] = useState<string | null>(null)

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ["app-settings"],
    queryFn: async () => {
      const res = await authorizedFetch(`${API_URL}/api/app-settings`)
      return await res.json() as AppSetting[]
    },
  })

  const updateMut = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) => {
      const res = await authorizedFetch(`${API_URL}/api/app-settings/${encodeURIComponent(key)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      })
      return await res.json() as AppSetting
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["app-settings"] })
      setEditing(null)
      setFormError(null)
    },
    onError: (err: Error) => setFormError(err.message),
  })

  function openEdit(setting: AppSetting) {
    setEditing(setting)
    setValueText(JSON.stringify(setting.value, null, 2))
    setFormError(null)
  }

  function save() {
    if (!editing) return
    try {
      updateMut.mutate({ key: editing.key, value: JSON.parse(valueText) })
    } catch {
      setFormError("El valor debe ser JSON válido. Para textos, usá comillas dobles.")
    }
  }

  return (
    <>
      <SiteHeader title="Configuraciones" />
      <main className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="mb-6 flex items-center gap-3">
          <Settings2Icon className="size-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Configuraciones</h1>
            <p className="text-sm text-muted-foreground">
              Valores administrables de configuraciones creadas mediante seeder.
            </p>
          </div>
        </div>

        <div className="min-w-0 overflow-hidden rounded-lg border bg-card">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[22%]">Clave</TableHead>
                <TableHead className="w-[13%]">Categoría</TableHead>
                <TableHead className="w-[35%]">Valor</TableHead>
                <TableHead className="w-[25%]">Descripción</TableHead>
                <TableHead className="w-9" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={5}>Cargando configuraciones...</TableCell></TableRow>
              )}
              {!isLoading && settings.length === 0 && (
                <TableRow><TableCell colSpan={5}>No hay configuraciones sembradas.</TableCell></TableRow>
              )}
              {settings.map((setting) => (
                <TableRow key={setting.id}>
                  <TableCell className="min-w-0 whitespace-normal wrap-break-word font-mono text-xs">{setting.key}</TableCell>
                  <TableCell className="min-w-0 whitespace-normal wrap-break-word">{setting.category}</TableCell>
                  <TableCell className="min-w-0 max-w-0 font-mono text-xs">
                    <div className="truncate" title={formatValue(setting.value)}>{formatValue(setting.value)}</div>
                  </TableCell>
                  <TableCell className="min-w-0 whitespace-normal wrap-break-word text-sm text-muted-foreground">{setting.description ?? "—"}</TableCell>
                  <TableCell>
                    {can("write", "config") && setting.isEditable && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(setting)} title="Editar">
                        <PencilIcon className="size-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <Sheet open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <SheetContent side="right" className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Editar configuración</SheetTitle>
            <SheetDescription>{editing?.key}</SheetDescription>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setting-value">Valor JSON</Label>
              <textarea
                id="setting-value"
                value={valueText}
                onChange={(event) => setValueText(event.target.value)}
                className="min-h-32 w-full rounded-md border bg-background px-3 py-2 font-mono text-sm outline-none focus:ring-2 focus:ring-ring"
                placeholder='Ejemplo: "valor" o 80 o true'
              />
              <p className="text-xs text-muted-foreground">
                Para objetos o arrays se puede ingresar JSON multilínea.
              </p>
            </div>
            {formError && <p className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive">{formError}</p>}
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button onClick={save} disabled={updateMut.isPending}>
              <SaveIcon className="mr-2 size-4" /> Guardar
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  )
}
